import { useCallback, useMemo, useState } from "react";
import { Box, Button, Select, Stack } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import useSWR from "swr";
import DataWrapper from "../../../../common/components/DataWrapper/DataWrapper";
import OperatorKpiCards from "../../components/OperatorKpiCards/OperatorKpiCards";
import { fetcher } from "../../../../services/fetcher";
import {
  endpoint,
  OPERATOR_OVERVIEW_DASHBOARD_ENDPOINT,
  operatorSearch,
  mapOperatorListRows,
  // formatRevenue,
  OPERATOR_TYPE_FILTER_OPTIONS,
  matchesOperatorTypeFilter,
} from "../../constants/operatorListConstants";

const Operators = () => {
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [stats, setStats] = useState({
    totalOperators: 0,
    activeOperators: 0,
    lowWalletOperators: 0,
  });
  const { data: dashboardData } = useSWR(
    OPERATOR_OVERVIEW_DASHBOARD_ENDPOINT,
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  const pickMetric = (source, keys, fallback = 0) => {
    for (const key of keys) {
      const value = source?.[key];
      if (value !== undefined && value !== null && value !== "") return value;
    }
    return fallback;
  };

  const kpiSource =
    dashboardData?.kpis ||
    dashboardData?.data?.kpis ||
    dashboardData?.results?.kpis ||
    dashboardData?.overview ||
    dashboardData;

  const handleData = useCallback((data) => {
    if (!data) return;
    const rows = mapOperatorListRows(data.results || []);
    setStats({
      totalOperators: data.count ?? rows.length,
      activeOperators: rows.filter(
        (item) => String(item.status).toLowerCase() === "active"
      ).length,
      lowWalletOperators: rows.filter(
        (item) => Number(item.wallet_balance) < 5000
      ).length,
    });
  }, []);

  const cards = useMemo(
    () => [
      {
        label: "Total Operator",
        value: Number(
          pickMetric(kpiSource, ["total_operators", "operators_count"], stats.totalOperators)
        ).toLocaleString("en-IN"),
      },
      {
        label: "Active Operator",
        value: Number(
          pickMetric(kpiSource, ["active_operators", "active_operator_count"], stats.activeOperators)
        ).toLocaleString("en-IN"),
      },
      {
        label: "Low Wallet Operator",
        value: Number(
          pickMetric(
            kpiSource,
            ["low_wallet_operators", "low_balance_operators"],
            stats.lowWalletOperators
          )
        ).toLocaleString("en-IN"),
      },
      {
        label: "Total Policy Issued",
        value: Number(
          pickMetric(kpiSource, ["policies_issued", "policy_issued", "policies_count"], 0)
        ).toLocaleString("en-IN"),
      },
      // {
      //   label: "Total Revenue",
      //   value: formatRevenue(
      //     pickMetric(kpiSource, ["total_revenue", "revenue_total", "revenue"], 0)
      //   ),
      // },
      {
        label: "Inactive Operator",
        value: Number(
          pickMetric(kpiSource, ["inactive_operators", "inactive_operator_count"], 0)
        ).toLocaleString("en-IN"),
      },
    ],
    [kpiSource, stats]
  );

  const isTypeFiltered = typeFilter !== "ALL";

  const extraQuery = useMemo(
    () => (isTypeFiltered ? { operator_type: typeFilter } : {}),
    [isTypeFiltered, typeFilter]
  );

  const filterRows = useCallback(
    (item) => matchesOperatorTypeFilter(item, typeFilter),
    [typeFilter]
  );

  const typeToolbar = (
    <Select
      value={typeFilter}
      onChange={(value) => setTypeFilter(value || "ALL")}
      data={OPERATOR_TYPE_FILTER_OPTIONS}
      w={160}
      allowDeselect={false}
    />
  );

  const createOperatorButton = (
    <Button
      component={Link}
      to="/operator/operators/create"
      variant="gradient"
      leftSection={<IconPlus size={16} />}
      gradient={{ from: "#0053A8", to: "#033F7C", deg: 270 }}
    >
      Create New Operator
    </Button>
  );

  const config = useMemo(
    () => ({
      fields: [
        { key: "operator_name", label: "Operator Name" },
        { key: "operator_type", label: "Type" },
        { key: "wallet_balance", label: "Wallet Balance" },
        { key: "policies", label: "Policies" },
        { key: "revenue", label: "Revenue" },
        { key: "status", label: "Status", type: "status" },
      ],
      rowClickTo: (row) => `/operator/operators/${row.uuid}/operator-profile`,
      actions: [
        // {
        //   label: "Profile",
        //   to: (row) => `/operator/operators/${row.uuid}/operator-profile`,
        // },
        {
          label: "Covers",
          to: (row) => `/operator/operators/${row.uuid}/operator-assign-covers`,
        },
        {
          label: "Wallet",
          to: (row) => `/operator/operators/${row.uuid}/wallet`,
        },
        {
          label: "Commission",
          to: (row) => `/operator/operators/${row.uuid}/commission`,
        },
        {
          label: "Policy Issued",
          to: (row) => `/operator/operators/${row.uuid}/policies-issued`,
        },
        {
          label: "Payments",
          to: (row) => `/operator/operators/${row.uuid}/operator-payments`,
        },
        {
          label: "QR Code",
          to: (row) => `/operator/operators/${row.uuid}/qr-code`,
        },
      ],
    }),
    []
  );

  const swrConfig = useMemo(
    () => ({
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 60000,
    }),
    []
  );

  return (
    <Stack gap={24} maw={1200} w="100%" mx="auto">
      <OperatorKpiCards cards={cards} />
      <Box mt={-12}>
        <DataWrapper
          endpoint={endpoint}
          searchEndpoint={operatorSearch}
          mapResults={mapOperatorListRows}
          onData={handleData}
          config={config}
          extraQuery={extraQuery}
          filterRows={isTypeFiltered ? filterRows : undefined}
          toolbarLeftContent={typeToolbar}
          toolbarRightContent={createOperatorButton}
          swrConfig={swrConfig}
        />
      </Box>
    </Stack>
  );
};

export default Operators;
